import { Component, effect, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../core/services/auth-service';
import { SubTenantService } from '../../core/services/subtenant-service';
import { UserService } from '../../core/services/user-service';
import { environment } from '../../../environments/environment';
import { DynamicForm } from '../../ui/dynamic-form/dynamic-form';
import { DynamicFormConfig, FormValue } from '../../ui/dynamic-form/dynamic-form.model';
import { tenant } from '../../../tenants/tenant';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'nexa-login-screen',
  imports: [DynamicForm, TranslatePipe],
  templateUrl: './login-screen.html',
  styleUrl: './login-screen.css',
})
export class LoginScreen {
  authService = inject(AuthService);
  subTenantService = inject(SubTenantService);
  userService = inject(UserService);
  router = inject(Router);
  private readonly translate = inject(TranslateService);
  appName = signal(environment.appName);
  appVersion = signal(environment.appVersion);
  appYear = signal(environment.appYear);

  errorMessage = signal<string | null>(null);
  isLoading = signal<boolean>(false);
  isNewPasswordRequired = signal<boolean>(false);
  showRecoveryForm = signal<boolean>(false);
  codeSent = signal<boolean>(false);
  currentUsername = signal<string>('');
  successMessage = signal<string>('');
  iconColor = signal<string>(tenant.theme.semantic?.form?.iconColor || '#003B55');
  savedUsername = signal<string>('');
  savedPassword = signal<string>('');
  rememberMeChecked = signal<boolean>(false);

  currentFormConfig = computed<DynamicFormConfig>(() => {
    if (this.showRecoveryForm() && !this.codeSent()) {
      return {
        fields: [
          {
            key: 'recoveryUsername',
            type: 'text',
            placeholder: this.translate.instant('LOGIN.USERNAME_EMAIL_LABEL'),
            required: true,
            errorMessage: this.translate.instant('LOGIN.EMAIL_REQUIRED_ERROR'),
            hint: this.translate.instant('LOGIN.USERNAME_RECOVERY_HINT'),
            showClear: true,
            order: 1,
            maxLength: 100,
          },
        ],
        layout: 'vertical',
        submitLabel: this.translate.instant('LOGIN.REQUEST_CODE_BTN'),
        showReset: false,
        iconColor: this.iconColor(),
        submitButtonColor: this.iconColor(),
      };
    }

    if (this.showRecoveryForm() && this.codeSent()) {
      return {
        fields: [
          {
            key: 'recoveryUsername',
            type: 'text',
            placeholder: this.translate.instant('LOGIN.USERNAME_EMAIL_LABEL'),
            required: true,
            errorMessage: this.translate.instant('LOGIN.EMAIL_REQUIRED_ERROR'),
            hint: this.translate.instant('LOGIN.USERNAME_RECOVERY_SHORT_HINT'),
            showClear: true,
            order: 1,
            maxLength: 100,
          },
          {
            key: 'recoveryCode',
            type: 'text',
            placeholder: this.translate.instant('LOGIN.VERIFICATION_CODE_LABEL'),
            required: true,
            hint: this.translate.instant('LOGIN.VERIFICATION_CODE_HINT'),
            showClear: true,
            order: 2,
            maxLength: 100,
          },
          {
            key: 'newPassword',
            type: 'password',
            placeholder: this.translate.instant('LOGIN.NEW_PASSWORD_LABEL'),
            required: true,
            hint: this.translate.instant('LOGIN.NEW_PASSWORD_HINT'),
            showClear: true,
            order: 3,
            maxLength: 100,
          },
          {
            key: 'confirmPassword',
            type: 'password',
            placeholder: this.translate.instant('LOGIN.CONFIRM_PASSWORD_LABEL'),
            required: true,
            hint: this.translate.instant('LOGIN.CONFIRM_PASSWORD_HINT'),
            showClear: true,
            order: 4,
            maxLength: 100,
          },
        ],
        layout: 'vertical',
        submitLabel: this.translate.instant('LOGIN.CHANGE_PASSWORD_BTN'),
        showReset: false,
        iconColor: this.iconColor(),
        submitButtonColor: this.iconColor(),
      };
    }

    if (this.isNewPasswordRequired()) {
      return {
        fields: [
          {
            key: 'newPassword',
            type: 'password',
            placeholder: this.translate.instant('LOGIN.NEW_PASSWORD_LABEL'),
            required: true,
            hint: this.translate.instant('LOGIN.NEW_PASSWORD_HINT'),
            showClear: true,
            order: 1,
            maxLength: 100,
          },
          {
            key: 'confirmPassword',
            type: 'password',
            placeholder: this.translate.instant('LOGIN.CONFIRM_PASSWORD_LABEL'),
            required: true,
            hint: this.translate.instant('LOGIN.CONFIRM_PASSWORD_HINT'),
            showClear: true,
            order: 2,
            maxLength: 100,
          },
        ],
        layout: 'vertical',
        submitLabel: this.translate.instant('LOGIN.CHANGE_PASSWORD_BTN'),
        showReset: false,
        iconColor: this.iconColor(),
        submitButtonColor: this.iconColor(),
      };
    }

    return {
      fields: [
        {
          key: 'username',
          type: 'text',
          placeholder: this.translate.instant('LOGIN.USERNAME_LABEL'),
          required: true,
          errorMessage: this.translate.instant('LOGIN.EMAIL_REQUIRED_ERROR'),
          hint: this.translate.instant('LOGIN.USERNAME_HINT'),
          showClear: true,
          order: 1,
          maxLength: 100,
          defaultValue: this.savedUsername(),
        },
        {
          key: 'password',
          type: 'password',
          placeholder: this.translate.instant('LOGIN.PASSWORD_LABEL'),
          required: true,
          hint: this.translate.instant('LOGIN.PASSWORD_HINT'),
          showClear: true,
          order: 2,
          maxLength: 100,
          defaultValue: this.savedPassword(),
        },
        {
          key: 'rememberMe',
          type: 'checkbox',
          label: this.translate.instant('LOGIN.REMEMBER_ME'),
          defaultValue: this.rememberMeChecked(),
          order: 3,
          actionLink: {
            label: this.translate.instant('LOGIN.RECOVER_PASSWORD'),
            href: '#',
            class: 'text-sm font-medium no-underline hover:underline',
          },
        },
      ],
      layout: 'vertical',
      submitLabel: this.translate.instant('LOGIN.SIGN_IN_BTN'),
      showReset: false,
      iconColor: this.iconColor(),
      submitButtonColor: this.iconColor(),
    };
  });

  formTitle = computed<string>(() => {
    if (this.isNewPasswordRequired()) {
      return this.translate.instant('LOGIN.TITLE_CHANGE_PASSWORD');
    }
    if (this.showRecoveryForm() && !this.codeSent()) {
      return this.translate.instant('LOGIN.TITLE_RECOVERY');
    }
    if (this.showRecoveryForm() && this.codeSent()) {
      return this.translate.instant('LOGIN.TITLE_RESET');
    }
    return this.translate.instant('LOGIN.TITLE_SIGN_IN');
  });

  formMessage = computed<string>(() => {
    if (this.isNewPasswordRequired()) {
      return this.translate.instant('LOGIN.MSG_CHANGE_PASSWORD');
    }
    if (this.showRecoveryForm() && !this.codeSent()) {
      return this.translate.instant('LOGIN.MSG_RECOVERY');
    }
    if (this.showRecoveryForm() && this.codeSent()) {
      return this.translate.instant('LOGIN.MSG_RESET');
    }
    return '';
  });

  constructor() {
    this.loadSavedCredentials();

    effect(() => {
      const error = this.authService.error();
      if (error) {
        this.errorMessage.set(error);
      }
    });

    effect(() => {
      this.isLoading.set(this.authService.isLoading());
    });
  }

  handleActionLinkClick(fieldKey: string): void {
    if (fieldKey === 'rememberMe') {
      this.toggleRecoveryForm();
    }
  }

  async handleFormSubmit(formValue: FormValue): Promise<void> {
    if (this.isNewPasswordRequired()) {
      await this.handleNewPasswordSubmit(formValue);
    } else if (this.showRecoveryForm() && !this.codeSent()) {
      await this.handleRequestPasswordReset(formValue);
    } else if (this.showRecoveryForm() && this.codeSent()) {
      await this.handleSubmitNewPassword(formValue);
    } else {
      await this.handleLogin(formValue);
    }
  }

  private async handleLogin(formValue: FormValue): Promise<void> {
    this.errorMessage.set(null);
    this.isLoading.set(true);

    try {
      const username = formValue['username'] as string;
      const password = formValue['password'] as string;
      const rememberMe = formValue['rememberMe'] as boolean;

      if (!username || !password) {
        this.errorMessage.set(this.translate.instant('LOGIN.ERR_NO_CREDENTIALS'));
        this.isLoading.set(false);
        return;
      }

      const output = await this.authService.login(username, password);

      if (rememberMe) {
        this.saveCredentials(username, password);
      } else {
        this.clearSavedCredentials();
      }

      if (output.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        this.currentUsername.set(username);
        this.isNewPasswordRequired.set(true);
        this.errorMessage.set(null);
      } else if (output.isSignedIn) {
        this.subTenantService.clearSubtenant();
        this.router.navigate(['/']);
      }
    } catch (error: any) {
      const errorMsg = this.authService.error() || error.message || this.translate.instant('LOGIN.ERR_LOGIN_FAILED');
      this.errorMessage.set(errorMsg);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async handleNewPasswordSubmit(formValue: FormValue): Promise<void> {
    this.errorMessage.set(null);

    const newPassword = formValue['newPassword'] as string;
    const confirmPassword = formValue['confirmPassword'] as string;

    if (!newPassword || !confirmPassword) {
      this.errorMessage.set(this.translate.instant('LOGIN.ERR_FILL_ALL_FIELDS'));
      return;
    }

    if (newPassword !== confirmPassword) {
      this.errorMessage.set(this.translate.instant('LOGIN.ERR_PASSWORDS_MISMATCH'));
      return;
    }

    this.isLoading.set(true);

    try {
      await this.authService.completeNewPasswordChallenge(newPassword);

      const token = await this.authService.getIdToken();
      if (token) {
        await firstValueFrom(this.userService.firstAccess(token));
      }

      this.isNewPasswordRequired.set(false);
      this.errorMessage.set(null);

      setTimeout(() => {
        this.router.navigate(['/']);
      }, 500);
    } catch (error: any) {
      console.error('Error changing password on first login:', error);
      this.errorMessage.set(this.authService.error() || this.translate.instant('LOGIN.ERR_CHANGE_PASSWORD_FAILED'));
    } finally {
      this.isLoading.set(false);
    }
  }

  private async handleRequestPasswordReset(formValue: FormValue): Promise<void> {
    this.errorMessage.set(null);

    const recoveryUsername = formValue['recoveryUsername'] as string;

    if (!recoveryUsername) {
      this.errorMessage.set(this.translate.instant('LOGIN.ERR_NO_USERNAME'));
      return;
    }

    this.isLoading.set(true);

    try {
      await this.authService.forgotPassword(recoveryUsername);
      this.codeSent.set(true);
      this.currentUsername.set(recoveryUsername);
      this.errorMessage.set(null);
    } catch (error: any) {
      console.error('Error requesting password reset:', error);
      this.errorMessage.set(this.authService.error() || this.translate.instant('LOGIN.ERR_SEND_CODE_FAILED'));
    } finally {
      this.isLoading.set(false);
    }
  }

  private async handleSubmitNewPassword(formValue: FormValue): Promise<void> {
    this.errorMessage.set(null);

    const recoveryUsername = formValue['recoveryUsername'] as string;
    const recoveryCode = formValue['recoveryCode'] as string;
    const newPassword = formValue['newPassword'] as string;
    const confirmPassword = formValue['confirmPassword'] as string;

    if (!recoveryUsername || !recoveryCode || !newPassword || !confirmPassword) {
      this.errorMessage.set(this.translate.instant('LOGIN.ERR_FILL_ALL_FIELDS'));
      return;
    }

    if (newPassword !== confirmPassword) {
      this.errorMessage.set(this.translate.instant('LOGIN.ERR_PASSWORDS_MISMATCH'));
      return;
    }

    this.isLoading.set(true);

    try {
      await this.authService.forgotPasswordSubmit(
        recoveryUsername,
        recoveryCode,
        newPassword
      );

      this.showRecoveryForm.set(false);
      this.codeSent.set(false);
      this.errorMessage.set(null);
      this.successMessage.set(this.translate.instant('LOGIN.SUCCESS_PASSWORD_CHANGED'));
    } catch (error: any) {
      console.error('Error submitting new password:', error);
      this.errorMessage.set(this.authService.error() || this.translate.instant('LOGIN.ERR_RESET_FAILED'));
    } finally {
      this.isLoading.set(false);
    }
  }

  toggleRecoveryForm(): void {
    this.showRecoveryForm.update(val => !val);
    this.codeSent.set(false);
    this.isNewPasswordRequired.set(false);
    this.errorMessage.set(null);
    this.successMessage.set('');
    this.currentUsername.set('');
  }

  iHaveACode(): void {
    this.showRecoveryForm.set(true);
    this.codeSent.set(true);
    this.errorMessage.set(null);
    this.successMessage.set('');
  }

  private saveCredentials(username: string, password: string): void {
    try {
      localStorage.setItem('rememberedUsername', username);
      localStorage.setItem('rememberedPassword', password);
      localStorage.setItem('rememberMe', 'true');
    } catch (error) {
      console.error('Error saving credentials to localStorage:', error);
    }
  }

  private loadSavedCredentials(): void {
    try {
      const savedUsername = localStorage.getItem('rememberedUsername');
      const savedPassword = localStorage.getItem('rememberedPassword');
      const rememberMe = localStorage.getItem('rememberMe') === 'true';

      if (savedUsername && savedPassword && rememberMe) {
        this.savedUsername.set(savedUsername);
        this.savedPassword.set(savedPassword);
        this.rememberMeChecked.set(true);
      }
    } catch (error) {
      console.error('Error loading credentials from localStorage:', error);
    }
  }

  private clearSavedCredentials(): void {
    try {
      localStorage.removeItem('rememberedUsername');
      localStorage.removeItem('rememberedPassword');
      localStorage.removeItem('rememberMe');
      this.savedUsername.set('');
      this.savedPassword.set('');
      this.rememberMeChecked.set(false);
    } catch (error) {
      console.error('Error clearing credentials from localStorage:', error);
    }
  }
}
